/**
 * A new online order reaches the till by itself (the owner, 2026-10-10: „ონლაინ
 * შეკვეთა საერთოდ არ მიჩვენებს მესიჯს ან სიახლეს სანამ მე არ შევალ" — a sound,
 * a message, and the module's count pulsing).
 *
 * Every device of the owner or a cashier where the shop's online store is on
 * asks, every 30 s, for the orders still „new" (unanswered). An order that was
 * not in the previous answer has ARRIVED: the till chimes and says so. The first
 * answer of a visit is the baseline, so a reload never chimes for old orders.
 * The menu shows how many are unanswered, and pulses while some came after this
 * device last had the orders page in view. That mark is the newest order's own
 * time (the server's clock), never this device's — a till's clock may be off.
 *
 * Pure: components/order-alerts.tsx asks, chimes and draws.
 */
import type { Key } from './i18n'

export type AlertOrder = { id: string; number: number; total: number; created_at: string }

/** What the rules below read — an order, or (1.45) a support request stamped with its sent time
 *  (lib/support-alerts.ts). */
type Stamped = { id: string; created_at: string }

/** How often a till asks. Telegram already brings an order at once; the till may take half a minute. */
export const ALERT_POLL_MS = 30_000

/** This device's „seen up to" mark, per shop. */
export const seenKey = (orgId: string) => `martepos:orders-seen:${orgId}`

export function arrivals<T extends { id: string }>(previous: ReadonlySet<string> | null, now: T[]): T[] {
  if (previous === null) return []
  return now.filter((o) => !previous.has(o.id))
}

export function unseenCount(now: readonly Stamped[], seenAt: string | null): number {
  if (seenAt === null) return now.length
  const mark = Date.parse(seenAt)
  return now.filter((o) => Date.parse(o.created_at) > mark).length
}

export function seenMark(now: readonly Stamped[], current: string | null): string | null {
  let mark = current
  for (const o of now) if (mark === null || Date.parse(o.created_at) > Date.parse(mark)) mark = o.created_at
  return mark
}

/** The newest order this device already announced (chime + message), per shop — shared by its tabs. */
export const announcedKey = (orgId: string) => `martepos:orders-announced:${orgId}`

/** Several tabs of one device hear the same order: only the first says so. The mark is the newest
 *  announced order's own time; what is not newer than it was said already, in another tab. */
export function toAnnounce<T extends Stamped>(came: T[], mark: string | null): { fresh: T[]; mark: string | null } {
  const fresh = mark === null ? came : came.filter((o) => Date.parse(o.created_at) > Date.parse(mark))
  return { fresh, mark: seenMark(fresh, mark) }
}

/** The 30-s tick, a window coming back and the orders page can each start a check. An answer that
 *  comes back after a NEWER one was delivered is dropped, so an older answer arriving late never moves
 *  the list back (or chimes again for an order it does not yet hold). Answers that come back in order
 *  each deliver — each is a true picture of its moment (Codex, 1.45 #4: the words said „only the
 *  newest started", stronger than this). A check that fails delivers nothing. */
export function latestOnly<T>(run: () => Promise<T>, deliver: (value: T) => void): () => Promise<void> {
  let started = 0
  let delivered = 0
  return async () => {
    const mine = ++started
    let value: T
    try { value = await run() } catch { return }
    if (mine < delivered) return
    delivered = mine
    deliver(value)
  }
}

/** One order: its number and total; several: how many. */
export function alertMessage(arrived: AlertOrder[]): { key: Key; vars: Record<string, string | number> } {
  if (arrived.length === 1) return { key: 'oo.alert.one', vars: { no: `#${arrived[0].number}`, sum: `₾${arrived[0].total.toFixed(2)}` } }
  return { key: 'oo.alert.many', vars: { n: arrived.length } }
}
